import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { Container } from '../../components/layout/ContainerFlex';
import TitleSection from '../../components/layout/TitleSection';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';

// Access the colors from the theme
const colors = TwTheme.extend.colors;
const eventGroupColors = {
  picking: colors.eventGroups.picking,
  putaway: colors.eventGroups.handling,
  packing: colors.eventGroups.labourTotal,
  shipping: colors.eventGroups.reordering,
  receiving: colors.eventGroups.total,
  replenishment: colors.eventGroups.travellingEndpoint,
  orders: colors.eventGroups.travellingHorizontal,
  breaks: colors.eventGroups.breaks,
  training: colors.eventGroups.training,
  maintenance: colors.eventGroups.maintenance,
  idle: colors.eventGroups.idle,
  overtime: colors.eventGroups.overtime,
  misc: colors.eventGroups.misc,
};

// Helper function to get a color for a process
const getProcessColor = process => {
  return eventGroupColors[process] || colors.eventGroups.misc;
};

// Map for pie chart colors
const processColorArray = [
  colors.eventGroups.picking,
  colors.eventGroups.handling,
  colors.eventGroups.travellingEndpoint,
  colors.eventGroups.reordering,
];

// Enhanced mock data with more detailed metrics
const mockActivityData = {
  // Daily activity data for each process type
  dailyActivity: [
    {
      date: '2024-01-01',
      orders: 120,
      picks: 450,
      putaways: 200,
      packing: 430,
      shipping: 118,
      receiving: 210,
      replenishment: 180,
    },
    {
      date: '2024-01-02',
      orders: 150,
      picks: 500,
      putaways: 250,
      packing: 470,
      shipping: 145,
      receiving: 260,
      replenishment: 220,
    },
    {
      date: '2024-01-03',
      orders: 130,
      picks: 480,
      putaways: 220,
      packing: 450,
      shipping: 128,
      receiving: 230,
      replenishment: 190,
    },
    {
      date: '2024-01-04',
      orders: 160,
      picks: 520,
      putaways: 280,
      packing: 490,
      shipping: 155,
      receiving: 290,
      replenishment: 240,
    },
    {
      date: '2024-01-05',
      orders: 140,
      picks: 490,
      putaways: 240,
      packing: 460,
      shipping: 137,
      receiving: 250,
      replenishment: 200,
    },
  ],
  // Activity distribution by type
  activityByType: [
    { id: 'Picking', value: 45, label: 'Picking' },
    { id: 'Putaway', value: 30, label: 'Putaway' },
    { id: 'Replenishment', value: 15, label: 'Replenishment' },
    { id: 'Returns', value: 10, label: 'Returns' },
  ],
  // Hourly activity patterns
  hourlyActivity: [
    {
      hour: 8,
      picking: 50,
      putaway: 30,
      packing: 25,
      shipping: 10,
      receiving: 40,
    },
    {
      hour: 9,
      picking: 120,
      putaway: 70,
      packing: 60,
      shipping: 30,
      receiving: 90,
    },
    {
      hour: 10,
      picking: 180,
      putaway: 100,
      packing: 140,
      shipping: 50,
      receiving: 120,
    },
    {
      hour: 11,
      picking: 150,
      putaway: 110,
      packing: 120,
      shipping: 60,
      receiving: 130,
    },
    {
      hour: 12,
      picking: 100,
      putaway: 80,
      packing: 90,
      shipping: 40,
      receiving: 70,
    },
    {
      hour: 13,
      picking: 140,
      putaway: 90,
      packing: 110,
      shipping: 50,
      receiving: 85,
    },
    {
      hour: 14,
      picking: 160,
      putaway: 100,
      packing: 130,
      shipping: 70,
      receiving: 95,
    },
    {
      hour: 15,
      picking: 190,
      putaway: 120,
      packing: 150,
      shipping: 90,
      receiving: 110,
    },
    {
      hour: 16,
      picking: 170,
      putaway: 90,
      packing: 130,
      shipping: 100,
      receiving: 80,
    },
    {
      hour: 17,
      picking: 90,
      putaway: 50,
      packing: 70,
      shipping: 80,
      receiving: 40,
    },
  ],
  // Performance metrics by process
  processByPerformance: {
    picking: [
      { date: '2024-01-01', rate: 42, accuracy: 97.5, efficiency: 88.2 },
      { date: '2024-01-02', rate: 45, accuracy: 98.1, efficiency: 89.5 },
      { date: '2024-01-03', rate: 43, accuracy: 97.8, efficiency: 87.9 },
      { date: '2024-01-04', rate: 46, accuracy: 98.3, efficiency: 90.1 },
      { date: '2024-01-05', rate: 44, accuracy: 98.0, efficiency: 88.6 },
    ],
    putaway: [
      { date: '2024-01-01', rate: 38, accuracy: 96.8, efficiency: 85.4 },
      { date: '2024-01-02', rate: 40, accuracy: 97.2, efficiency: 86.7 },
      { date: '2024-01-03', rate: 39, accuracy: 97.0, efficiency: 86.1 },
      { date: '2024-01-04', rate: 41, accuracy: 97.4, efficiency: 87.2 },
      { date: '2024-01-05', rate: 40, accuracy: 97.1, efficiency: 86.5 },
    ],
    packing: [
      { date: '2024-01-01', rate: 55, accuracy: 98.2, efficiency: 90.5 },
      { date: '2024-01-02', rate: 58, accuracy: 98.5, efficiency: 91.2 },
      { date: '2024-01-03', rate: 56, accuracy: 98.3, efficiency: 90.8 },
      { date: '2024-01-04', rate: 59, accuracy: 98.6, efficiency: 91.5 },
      { date: '2024-01-05', rate: 57, accuracy: 98.4, efficiency: 91.0 },
    ],
    receiving: [
      { date: '2024-01-01', rate: 35, accuracy: 96.5, efficiency: 84.8 },
      { date: '2024-01-02', rate: 37, accuracy: 96.9, efficiency: 85.6 },
      { date: '2024-01-03', rate: 36, accuracy: 96.7, efficiency: 85.2 },
      { date: '2024-01-04', rate: 38, accuracy: 97.1, efficiency: 86.0 },
      { date: '2024-01-05', rate: 37, accuracy: 96.8, efficiency: 85.5 },
    ],
  },
};

// Time frame options for filtering
const timeFrameOptions = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
];

// Process options for filtering
const processOptions = [
  { value: 'all', label: 'All Processes' },
  { value: 'picking', label: 'Picking' },
  { value: 'putaway', label: 'Putaway' },
  { value: 'packing', label: 'Packing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'receiving', label: 'Receiving' },
  { value: 'replenishment', label: 'Replenishment' },
];

// Metric type options
const metricOptions = [
  { value: 'volume', label: 'Volume' },
  { value: 'rate', label: 'Rate' },
  { value: 'accuracy', label: 'Accuracy' },
  { value: 'efficiency', label: 'Efficiency' },
];

export const WarehouseActivityCharts: React.FC = () => {
  const { t } = useTranslation('app');

  // State for filters
  const [timeFrame, setTimeFrame] = useState('day');
  const [selectedProcess, setSelectedProcess] = useState('all');
  const [metricType, setMetricType] = useState('volume');

  // Helper function to get chart keys based on selected process
  const getChartKeys = () => {
    if (selectedProcess === 'all') {
      return [
        'picks',
        'putaways',
        'packing',
        'shipping',
        'receiving',
        'replenishment',
      ];
    }
    return [selectedProcess];
  };

  // Get appropriate line data based on selected process and metric
  const getLineData = () => {
    const processKey = selectedProcess === 'all' ? 'picking' : selectedProcess;
    const metricKey = metricType === 'volume' ? 'rate' : metricType;

    if (selectedProcess === 'all') {
      return Object.keys(mockActivityData.processByPerformance).map(
        process => ({
          id: process,
          data: mockActivityData.processByPerformance[process].map(d => ({
            x: d.date,
            y: d[metricKey],
          })),
          color: getProcessColor(process),
        }),
      );
    }

    return [
      {
        id: processKey,
        data: mockActivityData.processByPerformance[processKey].map(d => ({
          x: d.date,
          y: d[metricKey],
        })),
        color: getProcessColor(processKey),
      },
    ];
  };

  // Get hourly data for selected processes
  const getHourlyData = () => {
    if (selectedProcess === 'all') {
      return mockActivityData.hourlyActivity.map(h => ({
        hour: h.hour,
        activity:
          Object.values(h).reduce(
            (sum, val) => (typeof val === 'number' ? sum + val : sum),
            0,
          ) - h.hour,
      }));
    }

    return mockActivityData.hourlyActivity.map(h => ({
      hour: h.hour,
      activity: h[selectedProcess] || 0,
    }));
  };

  // Custom color function for bar chart
  const getBarChartColors = ({ id }) => getProcessColor(id);

  // Custom colors for pie chart
  const getPieChartColors = ({ id }) => {
    const index = mockActivityData.activityByType.findIndex(
      item => item.id === id,
    );
    return processColorArray[index % processColorArray.length];
  };

  return (
    <Container col flex1>
      {/* Filters Section */}
      <TitleSection inPanelView title={t('Filter Options')}>
        <Stat className="mt-">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Time Frame Filter */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('Time Frame')}
              </label>
              <select
                className="bg-app-panel-dark w-full rounded-md border border-gray-600 p-2"
                value={timeFrame}
                onChange={e => setTimeFrame(e.target.value)}
              >
                {timeFrameOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>

            {/* Process Filter */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('Process')}
              </label>
              <select
                className="bg-app-panel-dark w-full rounded-md border border-gray-600 p-2"
                value={selectedProcess}
                onChange={e => setSelectedProcess(e.target.value)}
              >
                {processOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>

            {/* Metric Type Filter */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('Metric Type')}
              </label>
              <select
                className="bg-app-panel-dark w-full rounded-md border border-gray-600 p-2"
                value={metricType}
                onChange={e => setMetricType(e.target.value)}
              >
                {metricOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Stat>
      </TitleSection>

      <StatGroup classNameStatGroupItems="grid grid-cols-1 gap-1 md:grid-cols-2">
        {/* Daily Activity Bar Chart */}
        <Stat title={t('Daily Warehouse Activity')} classChildren="h-[400px]">
          <ResponsiveBar
            data={mockActivityData.dailyActivity}
            keys={getChartKeys()}
            indexBy="date"
            margin={{ top: 20, right: 130, bottom: 50, left: 60 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            colors={getBarChartColors}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: t('Date'),
              legendPosition: 'middle',
              legendOffset: 40,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('Count'),
              legendPosition: 'middle',
              legendOffset: -40,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom-right',
                direction: 'column',
                justify: false,
                translateX: 120,
                translateY: 0,
                itemsSpacing: 2,
                itemWidth: 100,
                itemHeight: 20,
                itemDirection: 'left-to-right',
                itemOpacity: 0.85,
                symbolSize: 20,
              },
            ]}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: colors.chart.ticks,
                  },
                },
                legend: {
                  text: {
                    fill: colors.chart.legend,
                  },
                },
              },
              grid: {
                line: {
                  stroke: colors.chart.grid,
                  strokeWidth: 0.3,
                },
              },
            }}
          />
        </Stat>

        {/* Activity Distribution Pie Chart */}
        <Stat title={t('By Process (Activity)')} classChildren="h-[400px]">
          <ResponsivePie
            data={mockActivityData.activityByType}
            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
            colors={getPieChartColors}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            borderWidth={1}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]],
            }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={colors.chart.text}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{
              from: 'color',
              modifiers: [['darker', 2]],
            }}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateX: 0,
                translateY: 56,
                itemsSpacing: 0,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: colors.chart.text,
                itemDirection: 'left-to-right',
                itemOpacity: 1,
                symbolSize: 18,
                symbolShape: 'circle',
              },
            ]}
          />
        </Stat>

        {/* Process Performance Line Chart */}
        <Stat
          title={t('Process Performance Metrics')}
          classChildren="h-[400px]"
        >
          <ResponsiveLine
            data={getLineData()}
            margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: t('Date'),
              legendOffset: 36,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend:
                metricType === 'volume'
                  ? t('Rate')
                  : t(metricType.charAt(0).toUpperCase() + metricType.slice(1)),
              legendOffset: -40,
              legendPosition: 'middle',
            }}
            enablePoints={true}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            colors={d => d.color || eventGroupColors.misc}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                justify: false,
                translateX: 0,
                translateY: 0,
                itemsSpacing: 0,
                itemDirection: 'left-to-right',
                itemWidth: 80,
                itemHeight: 20,
                itemOpacity: 0.75,
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: colors.chart.ticks,
                  },
                },
                legend: {
                  text: {
                    fill: colors.chart.legend,
                  },
                },
              },
              grid: {
                line: {
                  stroke: colors.chart.grid,
                  strokeWidth: 0.3,
                },
              },
            }}
          />
        </Stat>

        {/* Hourly Activity Line Chart */}
        <Stat title={t('Hourly Activity Pattern')} classChildren="h-[400px]">
          <ResponsiveLine
            data={[
              {
                id:
                  selectedProcess === 'all' ? 'all activity' : selectedProcess,
                data: getHourlyData().map(d => ({
                  x: d.hour,
                  y: d.activity,
                })),
                color:
                  selectedProcess === 'all'
                    ? colors.eventGroups.total
                    : getProcessColor(selectedProcess),
              },
            ]}
            margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('Hour'),
              legendOffset: 36,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('Activity Count'),
              legendOffset: -40,
              legendPosition: 'middle',
            }}
            enablePoints={true}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            colors={d => d.color || colors.eventGroups.total}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: colors.chart.ticks,
                  },
                },
                legend: {
                  text: {
                    fill: colors.chart.legend,
                  },
                },
              },
              grid: {
                line: {
                  stroke: colors.chart.grid,
                  strokeWidth: 0.3,
                },
              },
            }}
          />
        </Stat>
      </StatGroup>
    </Container>
  );
};

export default WarehouseActivityCharts;
